/**
 * WebAssembly 特性探测（DESIGN.md §2.7、§4.6）。
 *
 * SIMD 与 threads 对滤镜性能是数倍差距，但两者都可能不可用：
 * - **SIMD**：老浏览器不支持 `v128` 指令集。
 * - **threads**：需要 `SharedArrayBuffer`，而它要求页面跨源隔离（见
 *   {@link ./cross-origin-isolation}）。静态托管上 Service Worker 注入失败时就没有。
 *
 * 两者都**必须**有降级实现，探测本身也绝不抛异常——探测失败等同于"不支持"，
 * 调用方拿到的永远是布尔量，不需要 try/catch。
 *
 * 探测手段是用 `WebAssembly.validate` 校验一小段包含目标指令的字节码。
 * 这比 UA 嗅探可靠：它问的是"这个引擎能不能真的解析这条指令"。
 */

/**
 * 最小 SIMD 模块：`(func (result v128) i32.const 0; i8x16.splat)`。
 *
 * 关键字节：`0x7b` 是 v128 值类型，`0xfd 0x0f` 是 `i8x16.splat` 的 SIMD 前缀操作码。
 * 引擎不认识 SIMD 时会在校验阶段拒绝整个模块。
 *
 * 注意函数体末尾**不能** drop：签名声明了返回 v128，丢弃结果会让栈类型校验失败，
 * 于是所有引擎都拒绝该模块，探测永远返回 false（不支持 SIMD 也是这个结果，
 * 两者无法区分——这正是本文件必须有自测的原因）。
 */
const SIMD_PROBE: Uint8Array<ArrayBuffer> = Uint8Array.from([
  0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00, // 魔数 + 版本
  0x01, 0x05, 0x01, 0x60, 0x00, 0x01, 0x7b,       // type: () -> v128
  0x03, 0x02, 0x01, 0x00,                         // func: 一个函数用 type 0
  0x0a, 0x08, 0x01, 0x06, 0x00,                   // code section: 1 个函数体，长 6，无局部变量
  0x41, 0x00,                                     //   i32.const 0
  0xfd, 0x0f,                                     //   i8x16.splat
  0x0b,                                           // end
]);

/**
 * 最小 threads 模块：声明一块 `shared` 线性内存并执行 `i32.atomic.load`。
 *
 * 关键字节：内存 limits 的 flags 为 `0x03`（bit0=有上限，bit1=shared），
 * `0xfe 0x10` 是 `i32.atomic.load`。不支持 threads 的引擎会拒绝 shared 内存。
 */
const THREADS_PROBE: Uint8Array<ArrayBuffer> = Uint8Array.from([
  0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00, // 魔数 + 版本
  0x01, 0x04, 0x01, 0x60, 0x00, 0x00,             // type: () -> ()
  0x03, 0x02, 0x01, 0x00,                         // func
  0x05, 0x04, 0x01, 0x03, 0x01, 0x01,             // memory: shared, min=1, max=1
  0x0a, 0x0b, 0x01, 0x09, 0x00,                   // code section 头
  0x41, 0x00,                                     //   i32.const 0
  0xfe, 0x10, 0x02, 0x00,                         //   i32.atomic.load align=2 offset=0
  0x1a,                                           //   drop
  0x0b,                                           // end
]);

/** wasm 相关的宿主能力。全部是布尔量，缺失即降级，不抛异常。 */
export interface WasmFeatures {
  /** 引擎能否解析 128-bit SIMD 指令。 */
  readonly simd: boolean;
  /**
   * 引擎能否使用 shared 线性内存与原子指令。
   *
   * 注意这只回答"引擎支持吗"。实际要跑多线程 wasm 还需要
   * `SharedArrayBuffer` 真的可构造（即页面已跨源隔离），
   * 见 {@link detectWasmFeatures} 的 `sharedMemory` 参数。
   */
  readonly threads: boolean;
}

/**
 * `WebAssembly.validate` 的最小接口，便于测试注入。
 *
 * 探针常量显式注解为 `Uint8Array<ArrayBuffer>`：TS 5.9 起 `Uint8Array` 带
 * 缓冲区类型参数，不注解会被推断成 `Uint8Array<ArrayBufferLike>`，
 * 而它与 `BufferSource` 在严格模式下不相容。
 */
export interface WasmValidator {
  validate(bytes: BufferSource): boolean;
}

/**
 * 探测 wasm 特性。任何异常都被吞掉并视为"不支持"。
 *
 * @param validator 省略（`undefined`）时用全局 `WebAssembly`；显式传 `null`
 *   表示宿主没有 WebAssembly，返回全 false。
 *   两者必须区分：默认参数会把 `undefined` 吃掉，若用它表示"没有"就永远传不进去。
 * @param sharedMemoryAvailable `SharedArrayBuffer` 是否真的可用（跨源隔离的结果）。
 *   引擎支持 threads 但页面未隔离时，`threads` 仍为 `false`——因为此时
 *   `new WebAssembly.Memory({shared: true})` 会失败，报告 true 只会让调用方崩在更深处。
 */
export function detectWasmFeatures(
  validator?: WasmValidator | null,
  sharedMemoryAvailable = typeof SharedArrayBuffer === 'function',
): WasmFeatures {
  const engine = validator === undefined ? globalThis.WebAssembly : validator;
  if (engine === null || engine === undefined) return { simd: false, threads: false };

  const validate = (bytes: Uint8Array<ArrayBuffer>): boolean => {
    try {
      return engine.validate(bytes) === true;
    } catch {
      // 探测失败就是不支持。绝不让探测本身成为崩溃点。
      return false;
    }
  };

  return {
    simd: validate(SIMD_PROBE),
    threads: sharedMemoryAvailable && validate(THREADS_PROBE),
  };
}

/** 供调试与 BENCHMARKS 记录用的一行摘要。 */
export function describeWasmFeatures(features: WasmFeatures): string {
  const simd = features.simd ? 'SIMD' : 'no-SIMD';
  const threads = features.threads ? 'threads' : 'single-thread';
  return `wasm: ${simd}, ${threads}`;
}
